import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { BACKEND_URL } from '../config';
import useWorkspaceBasePath from '../hooks/useWorkspaceBasePath';
import DjamannLoader from '../components/DjamannLoader';
import { toast } from '../components/Toast';
import { confirm } from '../components/ConfirmDialog';
import { Printer, Save, CheckSquare, CheckCircle, Search, AlertTriangle, Hourglass, ArrowLeft, ScanBarcode, Camera, CameraOff, Package } from 'lucide-react';
import logger from '../utils/logger';

const BusinessInventoryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const routeBase = useWorkspaceBasePath();
  
  const [inventory, setInventory] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterEcart, setFilterEcart] = useState('all'); // all, ecarts, ok
  const [hasChanges, setHasChanges] = useState(false);
  const [showValidateModal, setShowValidateModal] = useState(false);
  const [settings, setSettings] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);

  // Scanner code-barres
  const [scanInput, setScanInput] = useState('');
  const [scanLoading, setScanLoading] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [lastScannedItem, setLastScannedItem] = useState(null);
  const scanInputRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const lastScanCodeRef = useRef('');
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const loadInventory = useCallback(async () => {
    try {
      const [res, settingsRes] = await Promise.all([
        api.get(`/business/inventories/${id}`),
        api.get('/business/settings').catch(() => ({ data: null }))
      ]);
      setInventory(res.data);
      setItems(res.data.items || []);
      if (settingsRes.data) setSettings(settingsRes.data);
    } catch (err) {
      logger.error(err);
      toast.error('Inventaire non trouvé');
      navigate(`${routeBase}/inventories`);
    } finally {
      setLoading(false);
    }
  }, [id, navigate, routeBase]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  // =====================
  // SCANNER CODE-BARRES
  // =====================
  const SCAN_COOLDOWN = 800; // 0.8s entre chaque scan (permet le comptage rapide)

  const handleScanSubmit = (code) => {
    const trimmedCode = (code || scanInput).trim();
    if (!trimmedCode || scanLoading) return;

    // Anti-rafale
    const now = Date.now();
    if (trimmedCode === lastScanCodeRef.current && now - lastScanTimeRef.current < SCAN_COOLDOWN) return;
    lastScanCodeRef.current = trimmedCode;
    lastScanTimeRef.current = now;

    // Chercher le produit dans la liste de l'inventaire par code_produit
    const currentItems = itemsRef.current;
    const idx = currentItems.findIndex(item =>
      item.product_code && item.product_code.toLowerCase() === trimmedCode.toLowerCase()
    );

    if (idx >= 0) {
      const item = currentItems[idx];
      // Incrémenter le stock compté de 1
      const newCount = (parseInt(item.stock_compte) || 0) + 1;
      handleCountChange(idx, newCount);
      setLastScannedItem({ ...item, stock_compte: newCount });
      playBeep(true);

      // Naviguer vers le produit (changer page si nécessaire)
      const pageOfItem = Math.floor(idx / itemsPerPage) + 1;
      if (pageOfItem !== currentPage) setCurrentPage(pageOfItem);

      // Scroll vers le produit
      setTimeout(() => {
        const el = document.getElementById(`inv-item-${item.product_id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } else {
      toast.error(`Aucun produit avec le code "${trimmedCode}" dans cet inventaire`);
      playBeep(false);
    }

    setScanInput('');
    scanInputRef.current?.focus();
  };

  const playBeep = (success) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = success ? 800 : 300;
      gain.gain.value = 0.3;
      osc.start();
      osc.stop(ctx.currentTime + (success ? 0.15 : 0.3));
    } catch (e) { /* ignore */ }
  };

  const startCamera = async () => {
    setShowCamera(true);
    setTimeout(async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        const scanner = new Html5Qrcode('inv-barcode-camera-reader');
        html5QrCodeRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 150 } },
          (decodedText) => handleScanSubmit(decodedText),
          () => {}
        );
      } catch (err) {
        logger.error('Erreur caméra:', err);
        toast.error('Impossible d\'accéder à la caméra');
        setShowCamera(false);
      }
    }, 100);
  };

  const stopCamera = async () => {
    if (html5QrCodeRef.current) {
      try { await html5QrCodeRef.current.stop(); } catch (e) {}
      html5QrCodeRef.current = null;
    }
    setShowCamera(false);
  };

  const handleCountChange = (index, value) => {
    const updated = [...items];
    const stockCompte = parseInt(value) || 0;
    const stockTheorique = parseInt(updated[index].stock_theorique) || 0;
    const ecart = stockCompte - stockTheorique;
    const prixUnit = parseFloat(updated[index].prix_unitaire) || 0;
    
    updated[index] = {
      ...updated[index],
      stock_compte: stockCompte,
      ecart: ecart,
      valeur_ecart: ecart * prixUnit
    };
    
    setItems(updated);
    setHasChanges(true);
  };

  const handleNoteChange = (index, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], notes: value };
    setItems(updated);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/business/inventories/${id}`, {
        items: items.map(item => ({
          product_id: item.product_id,
          product_nom: item.product_nom,
          product_code: item.product_code,
          stock_theorique: item.stock_theorique,
          stock_compte: item.stock_compte,
          prix_unitaire: item.prix_unitaire,
          notes: item.notes
        }))
      });
      setHasChanges(false);
      await loadInventory();
    } catch (err) {
      logger.error(err);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleFinalize = async () => {
    if (!await confirm({ message: 'Finaliser cet inventaire ?\n\nLe comptage sera marqué comme terminé. Vous pourrez ensuite le valider pour ajuster les stocks.' })) return;
    
    // Sauvegarder d'abord si des changements non sauvés
    if (hasChanges) await handleSave();
    
    try {
      await api.post(`/business/inventories/${id}/finalize`);
      await loadInventory();
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.error || 'Erreur lors de la finalisation');
    }
  };

  const handleValidate = async () => {
    try {
      await api.post(`/business/inventories/${id}/validate`);
      setShowValidateModal(false);
      await loadInventory();
      toast.success('Inventaire validé ! Les stocks ont été ajustés.');
    } catch (err) {
      logger.error(err);
      toast.error(err.response?.data?.error || 'Erreur lors de la validation');
    }
  };

  const handlePrint = () => {
    const statutLabel = inventory.statut === 'en_cours' ? 'En cours' : inventory.statut === 'termine' ? 'Terminé' : 'Validé';
    const logoUrl = settings?.logo ? (settings.logo.startsWith('http') ? settings.logo : `${BACKEND_URL}${settings.logo}`) : '';
    
    const allItems = items; // Print ALL items, not paginated
    const totalProduits = allItems.length;
    const totalEcarts = allItems.filter(i => parseInt(i.ecart) !== 0).length;
    const totalComptes = allItems.filter(i => parseInt(i.stock_compte) > 0 || inventory.statut === 'valide').length;
    const valEcartPos = allItems.filter(i => parseInt(i.ecart) > 0).reduce((s, i) => s + parseFloat(i.valeur_ecart || 0), 0);
    const valEcartNeg = allItems.filter(i => parseInt(i.ecart) < 0).reduce((s, i) => s + parseFloat(i.valeur_ecart || 0), 0);
    const valEcartTotal = allItems.reduce((s, i) => s + parseFloat(i.valeur_ecart || 0), 0);

    const itemsRows = allItems.map((item, idx) => {
      const ecart = parseInt(item.ecart) || 0;
      const ecartColor = ecart > 0 ? '#16a34a' : ecart < 0 ? '#dc2626' : '#6b7280';
      const valeurEcart = parseFloat(item.valeur_ecart || 0);
      return `<tr style="${ecart !== 0 ? 'background:#fffbeb;' : ''}">
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:center;font-size:12px;color:#9ca3af;">${idx + 1}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;">
          <div style="font-weight:600;font-size:13px;">${item.product_nom || ''}</div>
          ${item.product_code ? `<div style="font-size:11px;color:#9ca3af;">${item.product_code}</div>` : ''}
        </td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:500;">${item.stock_theorique}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:600;">${item.stock_compte}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:center;font-weight:700;color:${ecartColor};">${ecart !== 0 ? (ecart > 0 ? '+' : '') + ecart : '0'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;text-align:right;font-weight:600;color:${ecartColor};">${ecart !== 0 ? valeurEcart.toLocaleString() + ' XOF' : '-'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e5e7eb;font-size:11px;color:#6b7280;">${item.notes || ''}</td>
      </tr>`;
    }).join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) { toast.warning('Veuillez autoriser les popups pour imprimer.'); return; }

    printWindow.document.write(`<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Inventaire ${inventory.reference}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Segoe UI',Tahoma,Geneva,Verdana,sans-serif; color:#1f2937; padding:30px; font-size:13px; }
  .header { display:flex; justify-content:space-between; align-items:flex-start; margin-bottom:25px; padding-bottom:20px; border-bottom:3px solid #4f46e5; }
  .logo-section { display:flex; align-items:flex-start; gap:15px; }
  .logo-section img { width:70px; height:70px; object-fit:contain; border-radius:8px; }
  .company-name { font-size:22px; font-weight:700; color:#1f2937; }
  .company-info { font-size:12px; color:#6b7280; line-height:1.6; }
  .inv-title { text-align:right; }
  .inv-title h1 { font-size:26px; font-weight:800; color:#4f46e5; text-transform:uppercase; }
  .inv-title .ref { font-size:16px; color:#6b7280; font-weight:500; }
  .inv-meta { margin-top:8px; font-size:12px; color:#6b7280; }
  .inv-meta span { color:#1f2937; font-weight:600; }
  .stats-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:10px; margin-bottom:20px; }
  .stat-card { border:1px solid #e5e7eb; border-radius:8px; padding:10px; text-align:center; }
  .stat-label { font-size:11px; color:#6b7280; text-transform:uppercase; letter-spacing:0.5px; }
  .stat-value { font-size:18px; font-weight:700; margin-top:2px; }
  table { width:100%; border-collapse:collapse; margin-bottom:20px; }
  thead th { background:#f3f4f6; padding:8px; text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:0.5px; color:#6b7280; font-weight:600; border-bottom:2px solid #e5e7eb; }
  thead th.center { text-align:center; }
  thead th.right { text-align:right; }
  .summary { margin-top:20px; page-break-inside:avoid; }
  .summary-row { display:flex; justify-content:space-between; padding:6px 0; border-bottom:1px solid #f3f4f6; font-size:13px; }
  .summary-row.total { border-top:2px solid #1f2937; border-bottom:none; font-weight:700; font-size:15px; padding-top:10px; }
  .signatures { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-top:40px; page-break-inside:avoid; }
  .sig-block { text-align:center; }
  .sig-label { font-size:12px; color:#6b7280; font-weight:600; text-transform:uppercase; }
  .sig-line { border-bottom:1px solid #9ca3af; height:60px; margin-top:10px; }
  .sig-date { font-size:11px; color:#9ca3af; margin-top:5px; }
  .footer { margin-top:30px; padding-top:15px; border-top:1px solid #e5e7eb; text-align:center; font-size:10px; color:#9ca3af; }
  @media print { body { padding:15px; } .header { margin-bottom:15px; padding-bottom:10px; } }
</style></head><body>
  <div class="header">
    <div class="logo-section">
      ${logoUrl ? `<img src="${logoUrl}" alt="Logo">` : ''}
      <div>
        <div class="company-name">${settings?.nom_entreprise || 'Mon Entreprise'}</div>
        <div class="company-info">
          ${settings?.adresse ? settings.adresse + '<br>' : ''}
          ${settings?.telephone ? 'Tél: ' + settings.telephone + '<br>' : ''}
          ${settings?.email ? 'Email: ' + settings.email + '<br>' : ''}
          ${settings?.numero_registre ? 'RCCM: ' + settings.numero_registre + '<br>' : ''}
          ${settings?.numero_fiscal ? 'NIF: ' + settings.numero_fiscal : ''}
        </div>
      </div>
    </div>
    <div class="inv-title">
      <h1>Inventaire</h1>
      <div class="ref">${inventory.reference}</div>
      <div class="inv-meta">
        Date: <span>${new Date(inventory.date_inventaire).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</span><br>
        Statut: <span>${statutLabel}</span>
        ${inventory.notes ? '<br>Notes: <span>' + inventory.notes + '</span>' : ''}
        ${inventory.validated_at ? '<br>Validé le: <span>' + new Date(inventory.validated_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) + '</span>' : ''}
      </div>
    </div>
  </div>

  <div class="stats-grid">
    <div class="stat-card"><div class="stat-label">Produits</div><div class="stat-value" style="color:#1f2937;">${totalProduits}</div></div>
    <div class="stat-card"><div class="stat-label">Comptés</div><div class="stat-value" style="color:#2563eb;">${totalComptes} / ${totalProduits}</div></div>
    <div class="stat-card"><div class="stat-label">Écarts</div><div class="stat-value" style="color:#dc2626;">${totalEcarts}</div></div>
    <div class="stat-card"><div class="stat-label">Surplus (+)</div><div class="stat-value" style="color:#16a34a;">+${valEcartPos.toLocaleString()}</div></div>
    <div class="stat-card"><div class="stat-label">Manque (-)</div><div class="stat-value" style="color:#dc2626;">${valEcartNeg.toLocaleString()}</div></div>
  </div>

  <table>
    <thead>
      <tr>
        <th class="center" style="width:35px;">#</th>
        <th>Produit</th>
        <th class="center" style="width:80px;">Stock théorique</th>
        <th class="center" style="width:80px;">Stock compté</th>
        <th class="center" style="width:60px;">Écart</th>
        <th class="right" style="width:100px;">Valeur écart</th>
        <th style="width:120px;">Notes</th>
      </tr>
    </thead>
    <tbody>${itemsRows}</tbody>
  </table>

  <div class="summary">
    <div class="summary-row"><span>Total produits</span><span>${totalProduits}</span></div>
    <div class="summary-row"><span>Produits avec écarts</span><span style="color:#dc2626;font-weight:600;">${totalEcarts}</span></div>
    <div class="summary-row"><span>Surplus (entrées)</span><span style="color:#16a34a;font-weight:600;">+${valEcartPos.toLocaleString()} XOF</span></div>
    <div class="summary-row"><span>Manques (sorties)</span><span style="color:#dc2626;font-weight:600;">${valEcartNeg.toLocaleString()} XOF</span></div>
    <div class="summary-row total"><span>Impact financier net</span><span style="color:${valEcartTotal >= 0 ? '#16a34a' : '#dc2626'};">${valEcartTotal.toLocaleString()} XOF</span></div>
  </div>

  <div class="signatures">
    <div class="sig-block">
      <div class="sig-label">Responsable comptage</div>
      <div class="sig-line"></div>
      <div class="sig-date">Date: ___/___/______</div>
    </div>
    <div class="sig-block">
      <div class="sig-label">Responsable validation</div>
      <div class="sig-line"></div>
      <div class="sig-date">Date: ___/___/______</div>
    </div>
  </div>

  <div class="footer">
    ${settings?.nom_entreprise || 'Mon Entreprise'}${settings?.adresse ? ' - ' + settings.adresse : ''}${settings?.telephone ? ' - Tel: ' + settings.telephone : ''}<br>
    Document généré le ${new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
  </div>
</body></html>`);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.print(); };
  };

  if (loading) {
    return <DjamannLoader variant="section" />;
  }

  if (!inventory) return null;

  const isEditable = inventory.statut === 'en_cours';
  const isValidatable = inventory.statut === 'termine';
  const isValidated = inventory.statut === 'valide';

  // Filtrage
  const filteredItems = items.filter(item => {
    // Filtre recherche
    if (search) {
      const s = search.toLowerCase();
      if (!item.product_nom?.toLowerCase().includes(s) && !item.product_code?.toLowerCase().includes(s)) return false;
    }
    // Filtre écarts
    if (filterEcart === 'ecarts') return parseInt(item.ecart) !== 0;
    if (filterEcart === 'ok') return parseInt(item.ecart) === 0;
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedItems = filteredItems.slice(startIndex, startIndex + itemsPerPage);

  // Stats
  const totalProduits = items.length;
  const totalEcarts = items.filter(i => parseInt(i.ecart) !== 0).length;
  const totalComptes = items.filter(i => parseInt(i.stock_compte) > 0 || isValidated).length;
  const valeurEcartPos = items.filter(i => parseInt(i.ecart) > 0).reduce((s, i) => s + parseFloat(i.valeur_ecart || 0), 0);
  const valeurEcartNeg = items.filter(i => parseInt(i.ecart) < 0).reduce((s, i) => s + parseFloat(i.valeur_ecart || 0), 0);
  const valeurEcartTotal = items.reduce((s, i) => s + parseFloat(i.valeur_ecart || 0), 0);

  return (
    <div className="p-2 sm:p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Link to={`${routeBase}/inventories`} className="text-indigo-600 hover:text-indigo-800 text-sm">
              <ArrowLeft size={14} className="inline" /> Inventaires
            </Link>
          </div>
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
            {inventory.reference}
            <span className="ml-2">{
              isEditable ? <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-600">En cours</span> :
              isValidatable ? <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-600">Terminé</span> :
              <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-600">Validé</span>
            }</span>
          </h1>
          <p className="text-gray-500 text-xs sm:text-sm">
            {new Date(inventory.date_inventaire).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}
            {inventory.notes && ` - ${inventory.notes}`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={handlePrint}
            className="px-3 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 transition text-sm flex items-center gap-1"
          >
            <Printer size={16} /> Imprimer
          </button>
          {isEditable && (
            <>
              <button
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="px-3 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {saving ? <Hourglass size={14} className="inline" /> : <Save size={16} />} Sauvegarder
              </button>
              <button
                onClick={handleFinalize}
                className="px-3 py-2 bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition text-sm flex items-center gap-1"
              >
                <CheckSquare size={16} /> Finaliser
              </button>
            </>
          )}
          {isValidatable && (
            <button
              onClick={() => setShowValidateModal(true)}
              className="px-3 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition text-sm flex items-center gap-1"
            >
              <CheckCircle size={16} /> Valider & Ajuster stocks
            </button>
          )}
        </div>
      </div>

      {/* Stats résumé */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 md:grid-cols-5 md:gap-4 mb-4 sm:mb-6">
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-500">Produits</div>
          <div className="text-lg sm:text-xl font-bold text-gray-800">{totalProduits}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-500">Comptés</div>
          <div className="text-lg sm:text-xl font-bold text-blue-600">{totalComptes} / {totalProduits}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-500">Écarts</div>
          <div className="text-lg sm:text-xl font-bold text-red-600">{totalEcarts}</div>
        </div>
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-500">Surplus (+)</div>
          <div className="text-sm sm:text-lg font-bold text-green-600">+{valeurEcartPos.toLocaleString()} <span className="text-xs font-normal">XOF</span></div>
        </div>
        <div className="bg-white rounded-xl shadow p-2.5 sm:p-4">
          <div className="text-xs sm:text-sm text-gray-500">Manque (-)</div>
          <div className="text-sm sm:text-lg font-bold text-red-600">{valeurEcartNeg.toLocaleString()} <span className="text-xs font-normal">XOF</span></div>
        </div>
      </div>

      {/* Scanner code-barres (seulement si inventaire en cours) */}
      {isEditable && (
        <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 rounded-xl">
          <div className="flex items-center gap-2 mb-2.5">
            <ScanBarcode size={18} className="text-indigo-600" />
            <span className="font-medium text-indigo-800 text-sm">Scanner pour compter (+1)</span>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                ref={scanInputRef}
                type="text"
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleScanSubmit(); } }}
                placeholder="Scannez ou tapez le code-barres..."
                className="w-full border border-indigo-200 rounded-lg px-3 sm:px-4 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                autoComplete="off"
              />
            </div>
            <button
              type="button"
              onClick={() => handleScanSubmit()}
              disabled={!scanInput.trim()}
              className="px-3 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition text-sm"
            >
              <Search size={16} />
            </button>
            <button
              type="button"
              onClick={showCamera ? stopCamera : startCamera}
              className={`px-3 sm:px-4 py-2 rounded-lg transition text-sm font-medium ${
                showCamera
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-50'
              }`}
              title={showCamera ? 'Arrêter la caméra' : 'Scanner avec la caméra'}
            >
              {showCamera ? <CameraOff size={16} /> : <Camera size={16} />}
            </button>
          </div>

          {/* Zone caméra */}
          {showCamera && (
            <div className="mt-3 rounded-lg overflow-hidden border border-indigo-200">
              <div id="inv-barcode-camera-reader" className="w-full" style={{ minHeight: '200px' }}></div>
              <div className="bg-indigo-100 px-3 py-2 text-xs text-indigo-700 text-center">
                Pointez la caméra vers un code-barres
              </div>
            </div>
          )}

          {/* Dernier produit scanné */}
          {lastScannedItem && (
            <div className="mt-3 flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <Package size={14} className="text-green-600" />
              </div>
              <div className="flex-1 text-sm">
                <span className="font-medium text-green-800">{lastScannedItem.product_nom}</span>
                <span className="mx-2 text-green-400">•</span>
                <span className="text-green-600">Compté : <strong>{lastScannedItem.stock_compte}</strong></span>
              </div>
              <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-medium">+1</span>
            </div>
          )}
        </div>
      )}

      {/* Filtre produits */}
      <div className="bg-white rounded-xl shadow p-2.5 sm:p-4 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Rechercher un produit..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
              className="w-full pl-9 sm:pl-10 pr-8 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><Search size={14} /></span>
          </div>
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto">
            {[
              { value: 'all', label: `Tous (${totalProduits})` },
              { value: 'ecarts', label: <><AlertTriangle size={14} className="inline" /> Écarts ({totalEcarts})</> },
              { value: 'ok', label: <><CheckCircle size={14} className="inline" /> OK ({totalProduits - totalEcarts})</> }
            ].map(f => (
              <button
                key={f.value}
                onClick={() => { setFilterEcart(f.value); setCurrentPage(1); }}
                className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition flex-shrink-0 ${
                  filterEcart === f.value ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Pagination header + sélecteur */}
      {filteredItems.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
          <div className="text-xs sm:text-sm text-gray-500">
            {filteredItems.length} produit(s) - Page {currentPage}/{totalPages || 1}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Par page :</span>
            {[25, 50, 100, 200].map(n => (
              <button
                key={n}
                onClick={() => { setItemsPerPage(n); setCurrentPage(1); }}
                className={`px-2 py-1 rounded text-xs font-medium transition ${itemsPerPage === n ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Items - Desktop Table */}
      <div className="hidden md:block bg-white rounded-xl shadow overflow-hidden mb-4">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Produit</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Stock théorique</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Stock compté</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-600">Écart</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Valeur écart</th>
              {isEditable && <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Note</th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {paginatedItems.map((item, idx) => {
              const realIdx = items.findIndex(i => i.product_id === item.product_id);
              const ecart = parseInt(item.ecart) || 0;
              return (
                <tr key={item.product_id} id={`inv-item-${item.product_id}`} className={`hover:bg-gray-50 ${ecart !== 0 ? 'bg-amber-50/50' : ''} ${lastScannedItem?.product_id === item.product_id ? 'ring-2 ring-indigo-400' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-800">{item.product_nom}</div>
                    {item.product_code && <div className="text-xs text-gray-400">{item.product_code}</div>}
                  </td>
                  <td className="px-4 py-3 text-center text-sm font-medium text-gray-600">
                    {item.stock_theorique}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {isEditable ? (
                      <input
                        type="number"
                        min="0"
                        value={item.stock_compte}
                        onChange={(e) => handleCountChange(realIdx, e.target.value)}
                        className="w-20 text-center border rounded-lg py-1 px-2 text-sm font-medium focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    ) : (
                      <span className="font-medium">{item.stock_compte}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {ecart !== 0 ? (
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${ecart > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {ecart > 0 ? '+' : ''}{ecart}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-sm">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm">
                    {ecart !== 0 ? (
                      <span className={ecart > 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                        {parseFloat(item.valeur_ecart || 0).toLocaleString()} XOF
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  {isEditable && (
                    <td className="px-4 py-3">
                      <input
                        type="text"
                        placeholder="Note..."
                        value={item.notes || ''}
                        onChange={(e) => handleNoteChange(realIdx, e.target.value)}
                        className="w-full border rounded-lg py-1 px-2 text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Items - Mobile Cards */}
      <div className="md:hidden space-y-2">
        {paginatedItems.map((item, idx) => {
          const realIdx = items.findIndex(i => i.product_id === item.product_id);
          const ecart = parseInt(item.ecart) || 0;
          return (
            <div key={item.product_id} id={`inv-item-${item.product_id}`} className={`bg-white rounded-xl shadow-sm border overflow-hidden ${ecart !== 0 ? 'border-amber-200' : 'border-gray-100'} ${lastScannedItem?.product_id === item.product_id ? 'ring-2 ring-indigo-400' : ''}`}>
              <div className="p-3">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 text-sm">{item.product_nom}</div>
                    {item.product_code && <div className="text-xs text-gray-400">{item.product_code}</div>}
                  </div>
                  {ecart !== 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold flex-shrink-0 ${ecart > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {ecart > 0 ? '+' : ''}{ecart}
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-xs text-gray-500">Théorique</div>
                    <div className="text-sm font-bold text-gray-700">{item.stock_theorique}</div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Compté</div>
                    {isEditable ? (
                      <input
                        type="number"
                        min="0"
                        value={item.stock_compte}
                        onChange={(e) => handleCountChange(realIdx, e.target.value)}
                        className="w-full text-center border rounded-lg py-1 text-sm font-bold focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    ) : (
                      <div className="text-sm font-bold text-gray-700">{item.stock_compte}</div>
                    )}
                  </div>
                  <div>
                    <div className="text-xs text-gray-500">Valeur</div>
                    {ecart !== 0 ? (
                      <div className={`text-xs font-bold ${ecart > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {parseFloat(item.valeur_ecart || 0).toLocaleString()} XOF
                      </div>
                    ) : (
                      <div className="text-xs text-gray-400">-</div>
                    )}
                  </div>
                </div>

                {isEditable && (
                  <input
                    type="text"
                    placeholder="Note..."
                    value={item.notes || ''}
                    onChange={(e) => handleNoteChange(realIdx, e.target.value)}
                    className="w-full mt-2 border rounded-lg py-1 px-2 text-xs focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-3 px-3 sm:px-4 py-2.5 sm:py-3 bg-white rounded-xl shadow flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3">
          <div className="text-xs sm:text-sm text-gray-500">
            {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredItems.length)} sur {filteredItems.length}
          </div>
          <div className="flex gap-0.5 sm:gap-1 flex-wrap justify-center">
            <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm">««</button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm">«</button>
            {(() => {
              const pages = [];
              const showPages = new Set();
              showPages.add(1);
              showPages.add(totalPages);
              for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) showPages.add(i);
              const sortedPages = [...showPages].sort((a, b) => a - b);
              sortedPages.forEach((page, idx) => {
                if (idx > 0 && page - sortedPages[idx - 1] > 1) pages.push(<span key={`e-${page}`} className="px-1 sm:px-2 text-xs sm:text-sm text-gray-400">...</span>);
                pages.push(<button key={page} onClick={() => setCurrentPage(page)} className={`px-2 sm:px-3 py-1 rounded text-xs sm:text-sm ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}>{page}</button>);
              });
              return pages;
            })()}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm">»</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} className="px-2 sm:px-3 py-1 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm">»»</button>
          </div>
        </div>
      )}

      {/* Barre d'actions fixe en bas (mobile) */}
      {isEditable && (
        <div className="fixed bottom-0 left-0 right-0 md:hidden bg-white border-t shadow-lg p-3 flex gap-2 z-50">
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <><Hourglass size={14} className="inline" /> Sauvegarde...</> : <><Save size={16} /> Sauvegarder</>}
          </button>
          <button
            onClick={handleFinalize}
            className="flex-1 py-2.5 bg-amber-500 text-white rounded-xl font-medium text-sm"
          >
            <><CheckSquare size={16} /> Finaliser</>
          </button>
        </div>
      )}

      {/* Résumé final */}
      {!isEditable && (
        <div className="mt-4 bg-white rounded-xl shadow p-4 sm:p-6">
          <h3 className="text-sm sm:text-base font-bold text-gray-800 mb-3">Résumé de l'inventaire</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">Produits comptés</div>
              <div className="text-lg font-bold text-gray-800">{totalProduits}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">Produits avec écarts</div>
              <div className="text-lg font-bold text-red-600">{totalEcarts}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500">Impact financier net</div>
              <div className={`text-lg font-bold ${valeurEcartTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {valeurEcartTotal.toLocaleString()} XOF
              </div>
            </div>
          </div>
          {isValidated && inventory.validated_at && (
            <div className="mt-3 text-xs text-gray-500">
              <CheckCircle size={14} className="inline" /> Validé le {new Date(inventory.validated_at).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
      )}

      {/* Padding bottom pour la barre fixe mobile */}
      {isEditable && <div className="h-20 md:hidden"></div>}

      {/* Modal de validation */}
      {showValidateModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-auto">
            <div className="p-6">
              <div className="text-center mb-4">
                <span className="text-4xl"><AlertTriangle size={40} /></span>
                <h3 className="text-lg font-bold text-gray-800 mt-2">Valider l'inventaire ?</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Cette action va ajuster les stocks de vos produits selon le comptage effectué.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <div className="text-sm font-medium text-gray-700 mb-2">Résumé des ajustements :</div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Produits avec écarts</span>
                    <span className="font-bold text-red-600">{totalEcarts}</span>
                  </div>
                  {valeurEcartPos > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Surplus (entrées)</span>
                      <span className="font-bold text-green-600">+{valeurEcartPos.toLocaleString()} XOF</span>
                    </div>
                  )}
                  {valeurEcartNeg < 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Manques (sorties)</span>
                      <span className="font-bold text-red-600">{valeurEcartNeg.toLocaleString()} XOF</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-1 mt-1">
                    <span className="text-gray-800 font-medium">Impact net</span>
                    <span className={`font-bold ${valeurEcartTotal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {valeurEcartTotal.toLocaleString()} XOF
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs text-amber-700">
                <AlertTriangle size={14} className="inline" /> Cette action est irréversible. Les stocks seront mis à jour et des mouvements de stock seront enregistrés.
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowValidateModal(false)}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition text-sm font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={handleValidate}
                  className="flex-1 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 transition text-sm font-medium"
                >
                  <CheckCircle size={16} /> Valider
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessInventoryView;
